import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  GitPullRequest, Check, X, ArrowRight, Filter, MessageSquareQuote, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

const FIELD_LABELS = {
  title: "Title", summary: "Summary", content: "Content",
  category: "Category", department: "Department", tags: "Tags",
};

function DiffRow({ field, before, after }) {
  const renderVal = (v) => {
    if (v == null || v === "") return <span className="text-muted-foreground italic">empty</span>;
    if (Array.isArray(v)) return v.join(", ");
    return String(v);
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-[110px_1fr_1fr] gap-3 text-sm border-t border-border/60 py-3">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground pt-0.5">{FIELD_LABELS[field] || field}</div>
      <div className="border-l-2 border-destructive/40 pl-3 whitespace-pre-wrap break-words">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">before</span>
        <div className="opacity-70 line-through decoration-destructive/60">{renderVal(before)}</div>
      </div>
      <div className="border-l-2 border-emerald-500/40 pl-3 whitespace-pre-wrap break-words">
        <span className="text-[10px] uppercase tracking-wider text-emerald-500 block mb-1">proposed</span>
        <div>{renderVal(after)}</div>
      </div>
    </div>
  );
}

export default function Suggestions() {
  const { isAdmin } = useAuth();
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);   // suggestion id currently under review
  const [notes, setNotes] = useState("");
  const [current, setCurrent] = useState({});         // knowledge entries keyed by id, for diff

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/suggestions", { params: { status } });
      setRows(data);
      // Fetch current knowledge for each suggestion to build the diff.
      const ids = Array.from(new Set(data.map((r) => r.knowledge_id)));
      const results = await Promise.allSettled(ids.map((id) => api.get(`/knowledge/${id}`)));
      const nextCurrent = {};
      results.forEach((res, i) => {
        if (res.status === "fulfilled") nextCurrent[ids[i]] = res.value.data;
      });
      setCurrent(nextCurrent);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load suggestions");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const decide = async (sid, action) => {
    try {
      await api.post(`/suggestions/${sid}/${action}`, notes ? { review_notes: notes } : {});
      toast.success(action === "approve" ? "Merged into the entry" : "Rejected");
      setReviewing(null); setNotes("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    }
  };

  return (
    <div className="p-8 max-w-5xl" data-testid="suggestions-page">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <p className="eyebrow mb-3">Suggestions</p>
          <h1 className="font-display text-3xl flex items-center gap-3">
            <GitPullRequest className="w-7 h-7" /> Edit proposals
          </h1>
          <p className="mt-2 text-muted-foreground">
            Wiki-style review flow. Anyone in the workspace can propose an edit — Admins approve or reject.
          </p>
        </div>
      </header>

      <div className="flex items-center gap-1 mb-6 border-b border-border/60" data-testid="suggestions-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setStatus(t.key)}
            className={`px-3 py-2 text-sm border-b-2 transition-colors ${
              status === t.key
                ? "border-foreground text-foreground font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`suggestions-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card-flat p-8 text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card-flat p-10 text-center" data-testid="suggestions-empty">
          <Sparkles className="w-6 h-6 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No {status} suggestions yet.{" "}
            {status === "pending" && "Members can propose edits from any knowledge entry."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((r) => {
            const cur = current[r.knowledge_id];
            const isReviewing = reviewing === r.id;
            return (
              <div key={r.id} className="bento p-5" data-testid={`suggestion-row-${r.id}`}>
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                      #{r.id.slice(0, 8)} · proposed {new Date(r.proposed_at).toLocaleString()}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Link
                        to={`/app/knowledge?open=${r.knowledge_id}`}
                        className="font-medium hover:underline underline-offset-4"
                        data-testid={`suggestion-target-${r.id}`}
                      >
                        {r.knowledge_title || r.knowledge_id.slice(0, 8)}
                      </Link>
                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        by <span className="text-foreground/90">{r.proposed_by_name || r.proposed_by?.slice(0, 8)}</span>
                      </span>
                    </div>
                  </div>
                  <span
                    className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                      r.status === "pending" ? "border-amber-500/40 text-amber-500 bg-amber-500/5"
                      : r.status === "approved" ? "border-emerald-500/40 text-emerald-500 bg-emerald-500/5"
                      : "border-destructive/40 text-destructive bg-destructive/5"
                    }`}
                    data-testid={`suggestion-status-${r.id}`}
                  >
                    {r.status}
                  </span>
                </div>

                {r.reason && (
                  <div className="text-sm text-muted-foreground flex items-start gap-2 mb-3 border-l-2 border-border/60 pl-3">
                    <MessageSquareQuote className="w-3.5 h-3.5 mt-1 shrink-0" />
                    <span>{r.reason}</span>
                  </div>
                )}

                <div className="rounded-md border border-border/60 divide-y divide-border/60" data-testid={`suggestion-diff-${r.id}`}>
                  {Object.keys(r.changes || {}).map((f) => (
                    <DiffRow key={f} field={f} before={cur?.[f]} after={r.changes[f]} />
                  ))}
                </div>

                {r.status !== "pending" && r.review_notes && (
                  <div className="mt-3 text-xs text-muted-foreground border border-border/40 rounded p-3 bg-secondary/30">
                    <span className="uppercase tracking-wider text-[10px]">Reviewer notes</span>
                    <p className="mt-1 text-foreground/80 whitespace-pre-wrap">{r.review_notes}</p>
                    <p className="mt-2 text-[10px] font-mono">— {r.reviewed_by_name} · {new Date(r.reviewed_at).toLocaleString()}</p>
                  </div>
                )}

                {r.status === "pending" && isAdmin && (
                  <div className="mt-4 pt-4 border-t border-border/60">
                    {!isReviewing ? (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setReviewing(r.id)}
                          className="rounded-full"
                          data-testid={`suggestion-review-${r.id}`}
                        >
                          Review
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <Label htmlFor={`notes-${r.id}`} className="text-xs">Review notes (optional)</Label>
                          <Textarea
                            id={`notes-${r.id}`}
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Leave a note for the proposer…"
                            data-testid={`suggestion-notes-${r.id}`}
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={() => decide(r.id, "approve")}
                            className="rounded-full"
                            data-testid={`suggestion-approve-${r.id}`}
                          >
                            <Check className="w-4 h-4 mr-2" /> Approve & merge
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => decide(r.id, "reject")}
                            className="rounded-full text-destructive border-destructive/60 hover:bg-destructive hover:text-destructive-foreground"
                            data-testid={`suggestion-reject-${r.id}`}
                          >
                            <X className="w-4 h-4 mr-2" /> Reject
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => { setReviewing(null); setNotes(""); }}
                            className="rounded-full"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
